// Enhanced users table component with pending invites section
import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import { notifySuccess, notifyError } from '../Notification';
import { FiEdit } from 'react-icons/fi';
import GenericModal from "../genericModal/GenericModal";

import Button from "../Button/Button";
import './EnhancedUsersTable.css'; // We'll create this CSS file

const EnhancedUsersTable = ({ 
  users = [], // Default to empty array
  selectedBusiness, 
  hasAdminPermissions, 
  handleRemoveUser, 
  handleChangeUserRole,
  fetchUsers, // Pass this function from parent to refresh data
  userProfile // Add userProfile prop
}) => {
  const [pendingInvites, setPendingInvites] = useState([]);
  const [loadingInvites, setLoadingInvites] = useState(false);
  const [revokingInvite, setRevokingInvite] = useState(null);
  
  // Update role modal state
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [updatingRole, setUpdatingRole] = useState(false);

  // Ensure users is always an array
  const safeUsers = Array.isArray(users) ? users : [];

  // Fetch pending invites when component mounts or business changes
  useEffect(() => {
    if (selectedBusiness && selectedBusiness !== "All") {
      fetchPendingInvites();
    }
  }, [selectedBusiness]);

  const fetchPendingInvites = async () => {
    if (!selectedBusiness || selectedBusiness === "All") return;
    
    setLoadingInvites(true);
    try {
      const response = await api.get(`/invite/pending?business_id=${selectedBusiness}`);
      if (response.data.success) {
        setPendingInvites(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching pending invites:', error);
      // Only show error if it's not a permission issue
      if (error.response?.status !== 403) {
        notifyError('Failed to fetch pending invitations');
      }
      setPendingInvites([]); // Reset to empty array on error
    } finally {
      setLoadingInvites(false);
    }
  };

  const handleRevokeInvite = async (inviteId, email) => {
    if (!window.confirm(`Are you sure you want to revoke the invitation for ${email}?`)) {
      return;
    }

    setRevokingInvite(inviteId);
    try {
      const response = await api.delete(`/invite/revoke/${inviteId}`);
      if (response.data.success) {
        notifySuccess(response.data.message);
        // Refresh pending invites
        await fetchPendingInvites();
      }
    } catch (error) {
      console.error('Error revoking invite:', error);
      const errorMessage = error.response?.data?.message || 'Failed to revoke invitation';
      notifyError(errorMessage);
    } finally {
      setRevokingInvite(null);
    }
  };

  // Handle update role modal
  const handleOpenUpdateModal = (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setShowUpdateModal(true);
  };

  const handleCloseUpdateModal = () => {
    setShowUpdateModal(false);
    setSelectedUser(null);
    setNewRole('');
    setUpdatingRole(false);
  };

  const handleUpdateRole = async () => {
    if (!selectedUser || !newRole) return;

    setUpdatingRole(true);
    try {
      const response = await api.put(`/access/change-role/${selectedUser.email}`, { 
        role: newRole,
        business_id: selectedBusiness // Include business_id as updated backend expects
      });
      
      if (response.data.success) {
        notifySuccess(`User role updated to ${newRole} successfully`);
        handleCloseUpdateModal();
        if (fetchUsers) fetchUsers(); // Refresh the users list
      } else {
        throw new Error(response.data.message || 'Failed to update user role');
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update user role';
      notifyError(errorMessage);
    } finally {
      setUpdatingRole(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return 0;
    const now = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getRoleDisplayName = (role) => {
    const roleMap = {
      'TLA': 'Admin',
      'SLA': 'SLA', 
      'Business_Admin': 'Business Admin',
      'Admin': 'Admin',
      'R1': 'R1',
      'User': 'User'
    };
    return roleMap[role] || role;
  };

  // Debug logging
  console.log('EnhancedUsersTable props:', { 
    users: safeUsers, 
    usersLength: safeUsers.length, 
    usersType: typeof users,
    isArray: Array.isArray(users)
  });

  return (
    <div className="enhanced-users-table-container">
      {/* Current Users Section */}
      <div className="table-section">
        <div className="section-header">
          <h3 className="section-title">Current Users ({safeUsers.length})</h3>
        </div>
        
        {safeUsers.length > 0 ? (
          <table className="users-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Role</th>
                {hasAdminPermissions && hasAdminPermissions() && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {safeUsers.map((user, index) => (
                <tr key={user.id || user.email || index}>
                  <td>
                    <div className="user-info">
                      <div className="user-avatar">
                        {user.firstName || user.first_name ? 
                          (user.firstName || user.first_name).charAt(0).toUpperCase() : 
                          user.email ? user.email.charAt(0).toUpperCase() : '?'
                        }
                      </div>
                      <div className="user-details">
                        <div className="user-name">
                          {user.firstName || user.first_name || ''} {user.lastName || user.last_name || ''}
                        </div>
                        <div className="user-status">{user.status || 'Active'}</div>
                      </div>
                    </div>
                  </td>
                  <td>{user.email || 'N/A'}</td>
                  <td>
                    <span className={`role-badge role-${(user.role || '').toLowerCase().replace('_', '')}`}>
                      {getRoleDisplayName(user.role || 'User')}
                    </span>
                  </td>
                  {hasAdminPermissions && hasAdminPermissions() && (
                    <td>
                      <div className="action-buttons">
                        <Button 
                          variant="primary"
                          size="medium"
                          onClick={() => handleOpenUpdateModal(user)}
                          title="Update User Role"
                          icon={<svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path></svg>}
                        />
                        <Button 
                          variant="danger"
                          size="medium"
                          onClick={() => handleRemoveUser && handleRemoveUser(user.id || user.email)}
                          title="Remove User"
                          icon={<svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>}
                        />
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state-table">
            <p>No users found for this business</p>
          </div>
        )}
      </div>

      {/* Pending Invites Section */}
      {hasAdminPermissions && hasAdminPermissions() && (
        <div className="table-section pending-invites-section">
          <div className="section-header">
            <div className="section-title-container">
              <h3 className="section-title">
                Pending Invitations 
                {!loadingInvites && pendingInvites.length > 0 && (
                  <span className="count-badge">({pendingInvites.length})</span>
                )}
              </h3>
              <button 
                className="refresh-btn" 
                onClick={fetchPendingInvites}
                disabled={loadingInvites}
                title="Refresh pending invitations"
              >
                <svg 
                  width="16" 
                  height="16" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  className={loadingInvites ? 'spinning' : ''}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
                </svg>
              </button>
            </div>
          </div>

          {loadingInvites ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Loading pending invitations...</p>
            </div>
          ) : pendingInvites.length > 0 ? (
            <table className="invites-table">
              <thead>
                <tr>
                  <th>Invited User</th>
                  <th>Role</th>
                  <th>Invited By</th>
                  <th>Sent</th>
                  <th>Expires</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pendingInvites.map((invite, index) => {
                  const daysLeft = getDaysUntilExpiry(invite.expires_at);
                  const isExpiringSoon = daysLeft <= 2;
                  
                  return (
                    <tr key={invite.id || index} className="invite-row">
                      <td>
                        <div className="invite-user-info">
                          <div className="user-avatar pending">
                            {invite.email ? invite.email.charAt(0).toUpperCase() : '?'}
                          </div>
                          <div className="invite-details">
                            <div className="invite-email">{invite.email || 'N/A'}</div>
                            <div className="invite-status">Pending invitation</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`role-badge role-${(invite.role || '').toLowerCase().replace('_', '')}`}>
                          {getRoleDisplayName(invite.role || 'User')}
                        </span>
                      </td>
                      <td>
                        <span className="invited-by">{invite.invited_by || 'Unknown'}</span>
                      </td>
                      <td>
                        <span className="date-sent">
                          {formatDate(invite.created_at)}
                        </span>
                      </td>
                      <td>
                        <span className={`expiry-info ${isExpiringSoon ? 'expiring-soon' : ''}`}>
                          {daysLeft > 0 ? (
                            `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`
                          ) : (
                            'Expired'
                          )}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <Button 
                            variant="danger"
                            size="small"
                            onClick={() => handleRevokeInvite(invite.id, invite.email)}
                            disabled={revokingInvite === invite.id}
                            title="Revoke Invitation"
                            loading={revokingInvite === invite.id}
                            icon={<svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="empty-state-table">
              <div className="empty-state-icon">
                <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0H4m0 0l4-4m0 0l4 4m-4-4v12"></path>
                </svg>
              </div>
              <p>No pending invitations</p>
              <small>Sent invitations will appear here until they are accepted or expire.</small>
            </div>
          )}
        </div>
      )}

      {/* Update Role Modal */}
      <GenericModal 
        show={showUpdateModal} 
        onClose={handleCloseUpdateModal}
        header={
          <div className="modern-modal-header">
            <button className="modern-close-btn" onClick={handleCloseUpdateModal}>
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
            <div className="modern-invite-icon" style={{ backgroundColor: '#667eea' }}>
              <FiEdit size={28} color="white" />
            </div>
            <h2 className="modern-modal-title">Update User Role</h2>
            <p className="modern-modal-subtitle">Change the access level for this user</p>
          </div>
        }
        body={selectedUser && (
          <div className="update-role-form">
            <div className="user-info-modal">
              <div className="user-avatar">
                {selectedUser.firstName || selectedUser.first_name ? 
                  (selectedUser.firstName || selectedUser.first_name).charAt(0).toUpperCase() : 
                  selectedUser.email ? selectedUser.email.charAt(0).toUpperCase() : '?'
                }
              </div>
              <div className="user-details-modal">
                <div className="user-name-modal">
                  {selectedUser.firstName || selectedUser.first_name || ''} {selectedUser.lastName || selectedUser.last_name || ''}
                </div>
                <div className="user-email-modal">{selectedUser.email || 'N/A'}</div>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="roleSelect">Select New Role:</label>
              <select
                id="roleSelect"
                className="form-control role-select-modal"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                disabled={updatingRole}
              >
                <option value="User">User</option>
                <option value="Business_Admin">Business Admin</option>
                {(userProfile?.role === 'TLA' || userProfile?.role === 'SLA' || userProfile?.role === 'R1') && (
                  <>
                    <option value="R1">R1</option>
                    <option value="SLA">SLA</option>
                    <option value="TLA">TLA</option>
                  </>
                )}
              </select>
            </div>

            <div className="current-role-display">
              <small className="text-muted">
                Current role: <span className={`role-badge role-${(selectedUser.role || '').toLowerCase().replace('_', '')}`}>
                  {getRoleDisplayName(selectedUser.role || 'User')}
                </span>
              </small>
            </div>
          </div>
        )}
        footer={
          <>
            <Button variant="secondary" onClick={handleCloseUpdateModal} disabled={updatingRole}>
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleUpdateRole}
              disabled={updatingRole || newRole === selectedUser?.role}
              loading={updatingRole}
            >
              Update Role
            </Button>
          </>
        }
      />
    </div>
  );
};

export default EnhancedUsersTable;